/**
 * Web renderer for elka FormattedString — Phase 4.
 *
 * Returns a `DisplayableRun[]` (the displayable model), NOT React
 * nodes. A thin `<RenderDisplayable>` React wrapper (sibling file)
 * translates the data to JSX. Keeps this module testable from Node
 * without React and reusable from non-React surfaces.
 *
 * Spec: chores docs/plans/elka-display-spec.md §5.2 (renderer
 * output shapes), §5.4 (theme token tables).
 */

import type {
  CompactTimeSegment,
  CurrencySegment,
  DateTimeSegment,
  DurationSegment,
  FormattedString,
  ListSegment,
  NumberSegment,
  PluralSegment,
  RelativeSegment,
  Segment,
  SkeletonSegment,
  Style,
  StyledSegment,
} from "@elka/runtime";

/**
 * One styled run of plain text. Adjacent runs with byte-equal `style`
 * MAY be merged by a downstream cleanup pass — optional, not
 * required for correctness.
 */
export interface DisplayableRun {
  text: string;
  /** Present only when at least one resolved field is non-default. */
  style?: {
    color?: string;   // CSS value (e.g. "var(--color-warning)")
    weight?: string;  // CSS font-weight (e.g. "600")
    italic?: boolean; // resolved three-state; undefined = default
  };
}

export type Displayable = DisplayableRun[];

export interface RenderContext {
  locale: string;
  timeZone: string;
  /** Anchor for relative-time formatting. Defaults to `new Date()`. */
  now?: Date;
}

const OUTPUT_CODEPOINT_CAP = 4096;

/**
 * Resolves a possibly-invalid tz id. Per spec §5, an unloadable tz
 * MUST fall back to UTC with a single warning log per render — not
 * crash. Memoized per call site so the warn only fires once.
 */
function resolveTimeZone(tz: string): string {
  try {
    // Construct a probe formatter; throws RangeError on bad tz id.
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return tz;
  } catch {
    if (typeof console !== "undefined") {
      // eslint-disable-next-line no-console
      console.warn(`renderSegments: unloadable tz "${tz}", falling back to UTC`);
    }
    return "UTC";
  }
}

/**
 * Walk a FormattedString and emit the displayable model. Empty
 * segment arrays produce an empty array. Unknown segment kinds
 * degrade to "[?]" runs per spec §5.
 */
export function renderSegments(fs: FormattedString, ctx: RenderContext): Displayable {
  const resolvedCtx: RenderContext = { ...ctx, timeZone: resolveTimeZone(ctx.timeZone) };
  const out: DisplayableRun[] = [];
  for (const seg of fs.segments) {
    appendSegment(seg, resolvedCtx, /* parentStyle */ undefined, out);
  }
  return capOutput(out);
}

/** Plain-text projection: join all run text fields. */
export function renderString(fs: FormattedString, ctx: RenderContext): string {
  return renderSegments(fs, ctx).map(r => r.text).join("");
}

interface ResolvedStyle {
  color: string;   // empty string = inherit
  weight: string;  // empty string = inherit
  italic?: boolean;
}

function appendSegment(
  seg: Segment,
  ctx: RenderContext,
  parentStyle: ResolvedStyle | undefined,
  out: DisplayableRun[],
): void {
  // Dispatch in the order the proto declares the oneof fields.
  // Spec §5 dispatch table.
  if (seg.literal !== undefined) {
    emitRun(seg.literal.text, parentStyle, out);
    return;
  }
  if (seg.datetime !== undefined) {
    emitRun(formatDatetime(seg.datetime, ctx), parentStyle, out);
    return;
  }
  if (seg.datetimeSkeleton !== undefined) {
    emitRun(formatSkeleton(seg.datetimeSkeleton, ctx), parentStyle, out);
    return;
  }
  if (seg.number !== undefined) {
    emitRun(formatNumber(seg.number, ctx, "decimal"), parentStyle, out);
    return;
  }
  if (seg.percent !== undefined) {
    emitRun(formatNumber(seg.percent, ctx, "percent"), parentStyle, out);
    return;
  }
  if (seg.currency !== undefined) {
    emitRun(formatCurrency(seg.currency, ctx), parentStyle, out);
    return;
  }
  if (seg.duration !== undefined) {
    emitRun(formatDuration(seg.duration, ctx), parentStyle, out);
    return;
  }
  if (seg.relativeTime !== undefined) {
    emitRun(formatRelative(seg.relativeTime, ctx), parentStyle, out);
    return;
  }
  if (seg.list !== undefined) {
    emitRun(formatList(seg.list, ctx), parentStyle, out);
    return;
  }
  if (seg.plural !== undefined) {
    // Resolve CLDR category; recurse into the selected
    // FormattedString. parentStyle propagates (only the selected
    // form runs).
    const category = pluralCategory(seg.plural.count, ctx.locale);
    const form = seg.plural.forms[category] ?? seg.plural.forms["other"];
    if (form) {
      for (const child of form.segments) {
        appendSegment(child, ctx, parentStyle, out);
      }
    }
    return;
  }
  if (seg.styled !== undefined) {
    const merged = mergeStyles(parentStyle, seg.styled.style);
    for (const child of seg.styled.segments) {
      appendSegment(child, ctx, merged, out);
    }
    return;
  }
  if (seg.compactTime !== undefined) {
    emitRun(formatCompactTime(seg.compactTime, ctx), parentStyle, out);
    return;
  }
  // Unknown kind (older renderer, newer rule). Per spec §5: render
  // "[?]" and log a warning. We don't have the kind name from the
  // oneof tag.
  if (typeof console !== "undefined" && console.warn) {
    console.warn("renderSegments: unknown segment kind in FormattedString");
  }
  emitRun("[?]", parentStyle, out);
}

function emitRun(text: string, style: ResolvedStyle | undefined, out: DisplayableRun[]): void {
  if (text.length === 0) return; // skip empty runs
  if (!style) {
    out.push({ text });
    return;
  }
  const resolved: DisplayableRun["style"] = {};
  if (style.color) resolved.color = THEME_COLORS[style.color] ?? "inherit";
  if (style.weight) resolved.weight = THEME_WEIGHTS[style.weight] ?? "inherit";
  if (style.italic !== undefined) resolved.italic = style.italic;
  // If nothing was set, omit the style key entirely.
  if (!resolved.color && !resolved.weight && resolved.italic === undefined) {
    out.push({ text });
    return;
  }
  out.push({ text, style: resolved });
}

// ---- Style merge ----

function mergeStyles(parent: ResolvedStyle | undefined, child: Style): ResolvedStyle {
  return {
    // Empty string is the "inherit" sentinel — keep parent.
    color: child.color !== "" ? child.color : (parent?.color ?? ""),
    weight: child.weight !== "" ? child.weight : (parent?.weight ?? ""),
    // Three-state: child.italic set overrides; unset inherits.
    italic: child.italic !== undefined ? child.italic : parent?.italic,
  };
}

// ---- Kind-specific formatters ----

function formatDatetime(seg: DateTimeSegment, ctx: RenderContext): string {
  const date = new Date(Number(seg.instantUnix) * 1000);
  // Empty pattern: locale-medium default via Intl.
  if (!seg.pattern) {
    return new Intl.DateTimeFormat(ctx.locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: ctx.timeZone,
    }).format(date);
  }
  // Walk the CLDR UTS#35 concrete pattern token-by-token so literal
  // punctuation and field order survive. The earlier "translate to
  // Intl.DateTimeFormat options" approach dropped both because
  // Intl picks its own canonical order for the locale. Mirrors
  // Go's elka/render/datetime.go:formatICU byte-for-byte (per spec
  // §6 — both renderers must produce identical strings for the
  // patterns chores rules emit).
  return formatICUPattern(date, seg.pattern, ctx.timeZone, ctx.locale);
}

// ICU UTS#35 concrete-pattern formatter. Tokens supported (covers
// every pattern chores's seed rules emit + a small overflow):
//   y / yy / yyyy   year
//   M / MM / MMM / MMMM  month (number / Jan / January)
//   d / dd          day of month
//   E / EE / EEE    abbreviated weekday  ("Mon")
//   EEEE            full weekday        ("Monday")
//   H / HH          24-hour
//   h / hh          12-hour
//   m / mm          minute
//   s / ss          second
//   a               AM/PM marker
//   'text'          single-quoted literal
//   ''              literal apostrophe
// Whitespace + punctuation pass through verbatim. Unknown letter
// runs pass through as literal so the renderer stays total.
function formatICUPattern(
  date: Date,
  pattern: string,
  tz: string,
  locale: string,
): string {
  // Extract date parts in the host tz once. Intl.DateTimeFormat
  // is the cheapest portable path; we read components individually
  // and reassemble in the pattern's order.
  const partsByType = new Map<string, string>();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
    weekday: 'long',
  }).formatToParts(date);
  for (const p of parts) {
    partsByType.set(p.type, p.value);
  }
  const yearNum = parseInt(partsByType.get('year') ?? '0', 10);
  const monthNum = parseInt(partsByType.get('month') ?? '0', 10);
  const dayNum = parseInt(partsByType.get('day') ?? '0', 10);
  let hourNum = parseInt(partsByType.get('hour') ?? '0', 10);
  // formatToParts with hour12:false can return '24' at midnight on some Intl
  // implementations — coerce to 0.
  if (hourNum === 24) hourNum = 0;
  const minuteNum = parseInt(partsByType.get('minute') ?? '0', 10);
  const secondNum = parseInt(partsByType.get('second') ?? '0', 10);
  const longWeekday = partsByType.get('weekday') ?? '';

  const shortMonths = [
    '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  const longMonths = [
    '', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const shortWeekday = (w: string) => w.slice(0, 3);

  const pad2 = (n: number) => (n < 10 ? '0' + String(n) : String(n));
  const renderToken = (c: string, n: number): string => {
    switch (c) {
      case 'y':
        return n === 2 ? pad2(yearNum % 100) : String(yearNum);
      case 'M':
        if (n === 1) return String(monthNum);
        if (n === 2) return pad2(monthNum);
        if (n === 3) return shortMonths[monthNum];
        return longMonths[monthNum];
      case 'd':
        return n >= 2 ? pad2(dayNum) : String(dayNum);
      case 'E':
        return n >= 4 ? longWeekday : shortWeekday(longWeekday);
      case 'H':
        return n >= 2 ? pad2(hourNum) : String(hourNum);
      case 'h': {
        const h12 = hourNum % 12 || 12;
        return n >= 2 ? pad2(h12) : String(h12);
      }
      case 'm':
        return n >= 2 ? pad2(minuteNum) : String(minuteNum);
      case 's':
        return n >= 2 ? pad2(secondNum) : String(secondNum);
      case 'a':
        return hourNum < 12 ? 'AM' : 'PM';
      default:
        return c.repeat(n);
    }
  };

  let out = '';
  let i = 0;
  while (i < pattern.length) {
    const ch = pattern[i];
    // Single-quoted literal: '...' (with '' → ' inside).
    if (ch === "'") {
      if (i + 1 < pattern.length && pattern[i + 1] === "'") {
        out += "'";
        i += 2;
        continue;
      }
      i++;
      while (i < pattern.length) {
        if (pattern[i] === "'") {
          if (i + 1 < pattern.length && pattern[i + 1] === "'") {
            out += "'";
            i += 2;
            continue;
          }
          i++;
          break;
        }
        out += pattern[i];
        i++;
      }
      continue;
    }
    // Letter run = CLDR token. Use locale for context but most
    // tokens render from extracted parts already.
    if (/[A-Za-z]/.test(ch)) {
      let n = 1;
      while (i + n < pattern.length && pattern[i + n] === ch) n++;
      out += renderToken(ch, n);
      i += n;
      continue;
    }
    out += ch;
    i++;
  }
  // Touch `locale` so the var isn't reported as unused — kept in
  // the signature for future locale-sensitive token rendering
  // (e.g. month names from CLDR).
  void locale;
  return out;
}

function formatSkeleton(seg: SkeletonSegment, ctx: RenderContext): string {
  const date = new Date(Number(seg.instantUnix) * 1000);
  const opts = icuSkeletonToIntlOptions(seg.skeleton, ctx.timeZone);
  return new Intl.DateTimeFormat(ctx.locale, opts).format(date);
}

/// Bespoke chores compact-time format (see CompactTimeSegment in
/// elka segment.proto). NOT CLDR — hour 1..12 with no leading zero,
/// lower-case "a"/"p" period marker, drop ":mm" when minute == 0.
/// Mirrors elka/go/render/datetime.go::formatCompactTime and
/// SegmentRenderer.formatCompactTime byte-for-byte so all three
/// platforms produce identical output. Item 7b.
function formatCompactTime(seg: CompactTimeSegment, ctx: RenderContext): string {
  const date = new Date(Number(seg.instantUnix) * 1000);
  // Intl.DateTimeFormat lets us read hour/minute in the host tz
  // without manually walking the offset. hour12 omitted so we read
  // 0..23 directly.
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: ctx.timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const hourStr = parts.find((p) => p.type === "hour")?.value ?? "0";
  const minuteStr = parts.find((p) => p.type === "minute")?.value ?? "0";
  const hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);
  const suffix = hour >= 12 ? "p" : "a";
  let hour12 = hour % 12;
  if (hour12 === 0) hour12 = 12;
  if (minute === 0) return `${hour12}${suffix}`;
  return `${hour12}:${String(minute).padStart(2, "0")}${suffix}`;
}

function formatNumber(seg: NumberSegment, ctx: RenderContext, mode: "decimal" | "percent"): string {
  const opts: Intl.NumberFormatOptions = mode === "percent" ? { style: "percent" } : { style: "decimal" };
  // ICU pattern → Intl options: chores only uses simple grouping +
  // optional fractional digits. Map the digit counts; anything else
  // falls back to defaults.
  if (seg.pattern && mode === "decimal") {
    Object.assign(opts, icuNumberPatternToIntlOptions(seg.pattern));
  }
  const value = seg.intValue !== undefined ? Number(seg.intValue) :
                seg.doubleValue !== undefined ? seg.doubleValue : NaN;
  if (Number.isNaN(value)) return "?";
  return new Intl.NumberFormat(ctx.locale, opts).format(value);
}

function formatCurrency(seg: CurrencySegment, ctx: RenderContext): string {
  return new Intl.NumberFormat(ctx.locale, {
    style: "currency",
    currency: seg.currencyCode,
  }).format(seg.value);
}

function formatDuration(seg: DurationSegment, ctx: RenderContext): string {
  // Intl.DurationFormat is Stage 3 (Chrome 129+ / Safari 18+ / Node 22+).
  // Phase 4 prefers the native API when available; falls back to a
  // hand-rolled join otherwise. The corpus per-platform expected files
  // (§6) catch divergence between this and Intl.DurationFormat's
  // CLDR-version output.
  const ms = Number(seg.valueMs);
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  switch (seg.style) {
    case "hms":     return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
    case "hm":      return `${pad2(h)}:${pad2(m)}`;
    case "compact": return h > 0 ? `${h}h ${m}m` : `${m}m`;
    case "long":    return formatDurationLong(h, m, s, ctx.locale);
    default:        return `${pad2(h)}:${pad2(m)}`;
  }
}

function formatDurationLong(h: number, m: number, s: number, locale: string): string {
  // Use Intl.RelativeTimeFormat as a vehicle for the locale-aware
  // unit names; coalesce sub-fields manually. Hand-rolled because
  // Intl.DurationFormat isn't universally available yet.
  const fmt = new Intl.RelativeTimeFormat(locale, { style: "long" });
  const parts: string[] = [];
  // Hack: RelativeTimeFormat emits e.g. "in 1 hour" — strip the
  // direction word by formatting & trimming.
  const pieces: Array<[number, "hour" | "minute" | "second"]> = [];
  if (h > 0) pieces.push([h, "hour"]);
  if (m > 0) pieces.push([m, "minute"]);
  if (s > 0 || pieces.length === 0) pieces.push([s, "second"]);
  for (const [n, unit] of pieces) {
    const phrase = fmt.format(n, unit).replace(/^in\s+/i, "").replace(/\s+ago$/i, "");
    parts.push(phrase);
  }
  return parts.join(", ");
}

function formatRelative(seg: RelativeSegment, ctx: RenderContext): string {
  const target = new Date(Number(seg.instantUnix) * 1000);
  const anchorMs = seg.anchorUnix !== undefined
    ? Number(seg.anchorUnix) * 1000
    : (ctx.now ?? new Date()).getTime();
  const deltaSec = Math.round((target.getTime() - anchorMs) / 1000);
  // Pick the largest unit that fits.
  const absSec = Math.abs(deltaSec);
  const style: Intl.RelativeTimeFormatStyle =
    seg.style === "narrow" ? "narrow" :
    seg.style === "short" ? "short" : "long";
  const fmt = new Intl.RelativeTimeFormat(ctx.locale, { style });
  if (absSec < 60) return fmt.format(deltaSec, "second");
  if (absSec < 3600) return fmt.format(Math.round(deltaSec / 60), "minute");
  if (absSec < 86400) return fmt.format(Math.round(deltaSec / 3600), "hour");
  if (absSec < 30 * 86400) return fmt.format(Math.round(deltaSec / 86400), "day");
  if (absSec < 365 * 86400) return fmt.format(Math.round(deltaSec / (30 * 86400)), "month");
  return fmt.format(Math.round(deltaSec / (365 * 86400)), "year");
}

function formatList(seg: ListSegment, ctx: RenderContext): string {
  const items = seg.items.map(fs => renderString(fs, ctx));
  const style: Intl.ListFormatStyle =
    seg.style === "narrow" ? "narrow" :
    seg.style === "or" ? "long" : "long";
  const type: Intl.ListFormatType = seg.style === "or" ? "disjunction" : "conjunction";
  return new Intl.ListFormat(ctx.locale, { style, type }).format(items);
}

// ---- ICU pattern → Intl options ----

function icuPatternToIntlOptions(pattern: string, tz: string): Intl.DateTimeFormatOptions {
  if (pattern === "") {
    return { dateStyle: "medium", timeStyle: "short", timeZone: tz };
  }
  // Heuristic map for the patterns chores actually uses. Anything
  // else falls back to medium+short.
  const opts: Intl.DateTimeFormatOptions = { timeZone: tz };
  if (/M{3,}/.test(pattern)) opts.month = "short";
  else if (/M{2}/.test(pattern)) opts.month = "2-digit";
  else if (/M/.test(pattern)) opts.month = "numeric";
  if (/d{2}/.test(pattern)) opts.day = "2-digit";
  else if (/d/.test(pattern)) opts.day = "numeric";
  if (/y{4}/.test(pattern)) opts.year = "numeric";
  else if (/y{2}/.test(pattern)) opts.year = "2-digit";
  if (/H{2}/.test(pattern)) { opts.hour = "2-digit"; opts.hour12 = false; }
  else if (/H/.test(pattern)) { opts.hour = "numeric"; opts.hour12 = false; }
  if (/m{2}/.test(pattern)) opts.minute = "2-digit";
  else if (/m/.test(pattern)) opts.minute = "numeric";
  if (/E{4,}/.test(pattern)) opts.weekday = "long";
  else if (/E/.test(pattern)) opts.weekday = "short";
  return opts;
}

function icuSkeletonToIntlOptions(skeleton: string, tz: string): Intl.DateTimeFormatOptions {
  // Skeletons use the same field letters as concrete patterns but
  // without literal punctuation. Re-use the concrete mapper — it
  // returns the same options for the same letters.
  return icuPatternToIntlOptions(skeleton, tz);
}

function icuNumberPatternToIntlOptions(pattern: string): Intl.NumberFormatOptions {
  const opts: Intl.NumberFormatOptions = {};
  if (pattern.includes(",")) opts.useGrouping = true;
  const dot = pattern.indexOf(".");
  if (dot >= 0) {
    const decimals = pattern.substring(dot + 1);
    const min = (decimals.match(/0/g) ?? []).length;
    const max = decimals.replace(/[^0#]/g, "").length;
    opts.minimumFractionDigits = min;
    opts.maximumFractionDigits = max;
  }
  return opts;
}

function pluralCategory(count: string, locale: string): string {
  const n = Number(count);
  return new Intl.PluralRules(locale).select(n);
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

// ---- Theme token tables (spec §5.4) ----

const THEME_COLORS: Record<string, string> = {
  primary:   "var(--color-text-primary)",
  secondary: "var(--color-text-secondary)",
  muted:     "var(--color-text-muted)",
  success:   "var(--color-success)",
  warning:   "var(--color-warning)",
  danger:    "var(--color-danger)",
};

const THEME_WEIGHTS: Record<string, string> = {
  regular:  "400",
  semibold: "600",
  bold:     "700",
};

// ---- Output cap ----

function capOutput(runs: DisplayableRun[]): DisplayableRun[] {
  let total = 0;
  for (let i = 0; i < runs.length; i++) {
    const run = runs[i];
    const len = Array.from(run.text).length; // codepoint count
    if (total + len <= OUTPUT_CODEPOINT_CAP) {
      total += len;
      continue;
    }
    // Truncate this run; drop the rest.
    const remaining = OUTPUT_CODEPOINT_CAP - total;
    const truncatedText = Array.from(run.text).slice(0, remaining).join("");
    const head = runs.slice(0, i);
    head.push({ ...run, text: truncatedText + "…" });
    return head;
  }
  return runs;
}
